import { useEffect, useState } from 'react';
import { Modal } from '../Modal';
import { Link } from 'react-router';
import { useAppUser } from '../../auth/AppUserContext';
import { SubmissionKey } from '../../data/clientKey';
import { latestReopen, type ReopenNote } from '../../data/reopenReason';
import { getTaskThread } from '../../data/taskComments';
import { ReopenReason } from './ReopenReason';
import { completeTask, type ServerTask } from '../../data/tasks';
import { isPersonName, NAME_REQUIRED_MESSAGE } from '../../data/typedName';
import { getVisibleStores } from '../../data/stores';
import type { Store } from '../../data/types';
import { ActionButton } from '../ActionButton';
import { completesOnStoreBehalf, ON_BEHALF_CONFIRM } from './onBehalf';

/**
 * Completing a task records WHO did the work by name — store accounts are
 * shared, so the sign-in identity isn't enough (spec §6). Shared by the
 * Tasks hub and the store/district detail pages.
 */
export function CompleteTaskModal({
  task,
  onClose,
  onDone,
}: {
  task: ServerTask;
  onClose: () => void;
  onDone: () => void;
}) {
  const { user } = useAppUser();
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  // Shown inside the dialog — the page's own error line sits behind it.
  const [saveError, setSaveError] = useState<string | null>(null);
  // The "completed" event's key: the same while retrying the same completion (src/data/clientKey.ts).
  const [eventKey] = useState(() => new SubmissionKey('ev'));
  // A shared store account must name a person, not itself (owner ruling
  // 2026-10-22; the server refuses it too). Personal accounts: the server
  // records their own name whatever is typed.
  const shared = user?.role === 'StoreTeam';
  const nameOk = name.trim() !== '' && (!shared || user === null || isPersonName(name, user));
  // A reopened task: quote why (owner ruling 2026-10-22). This one task's
  // thread, read when the dialog opens — never a query per list row.
  const [reopen, setReopen] = useState<ReopenNote | null>(null);
  useEffect(() => {
    if (task.status !== 'reopened') return;
    let live = true;
    getTaskThread(task.id)
      .then((t) => {
        if (live) setReopen(latestReopen(t));
      })
      .catch(() => undefined); // best effort — completing never waits on it
    return () => {
      live = false;
    };
  }, [task.id, task.status]);

  // The task's store (cached read, shared with every name label): a DM
  // completing the store account's task is asked first (owner ruling 2026-10-25).
  const [store, setStore] = useState<Store | null>(null);
  useEffect(() => {
    if (!user) return;
    let live = true;
    getVisibleStores(user, { includeInactive: true })
      .then((all) => {
        if (live) setStore(all.find((s) => Number(s.id) === task.storeId) ?? null);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [user, task.storeId]);
  // Errors show after a try, not before typing (final run).
  const [tried, setTried] = useState(false);

  // "Proof required" (B1, 2026-10-25): the proof goes up on the task
  // page — completing from a list would only be refused by the server.
  if (task.proofRequired) {
    return (
      <Modal onClose={onClose}>
          <h2>Complete task</h2>
          <p>{task.title}</p>
          <p>This task needs a photo or PDF. Open the task to add one and complete it.</p>
          <div className="modal-actions">
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            <Link className="btn btn-primary" to={`/tasks/${task.id}`}>
              Open the task
            </Link>
          </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} dirty={name.trim() !== '' || notes.trim() !== ''}>
        <h2>Complete task</h2>
        <p>{task.title}</p>
        {reopen && <ReopenReason note={reopen} />}
        <div className="form-grid">
          <label>
            Completed by (your name)
            <input
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
              placeholder="First and last name"
            />
            {(tried || (shared && name.trim() !== '')) && !nameOk && (
              <span className="error" style={{ fontSize: '0.85rem' }}>
                {shared ? NAME_REQUIRED_MESSAGE : 'Type your name.'}
              </span>
            )}
          </label>
          <label className="span-2">
            Completion comment (required)
            <textarea
              className="q-input"
              rows={2}
              required
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What did you do? Add a photo if you can."
            />
            {tried && !notes.trim() && (
              <span className="error" style={{ fontSize: '0.85rem' }}>
                A completion comment is required.
              </span>
            )}
          </label>
        </div>
        {/* Only on a store's shared account, and not twice — the name error says it too (UI review U196/U203). */}
        {shared && !((tried || name.trim() !== '') && !nameOk) && (
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            Store accounts are shared — the name records who actually did the
            work.
          </p>
        )}
        {saveError && (
          <p className="error" role="alert">
            {saveError}
          </p>
        )}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <ActionButton
            className="btn btn-primary"
            busyLabel="Completing…"
            onAction={async () => {
              setSaveError(null);
              if (!nameOk || !notes.trim()) {
                setTried(true);
                return;
              }
              // Store not loaded yet → read it (cached) rather than skip the question (QA 2026-10-25).
              const taskStore =
                store ??
                (user
                  ? await getVisibleStores(user, { includeInactive: true })
                      .then((all) => all.find((st) => Number(st.id) === task.storeId) ?? null)
                      .catch(() => null)
                  : null);
              if (completesOnStoreBehalf(task, taskStore, user) && !window.confirm(ON_BEHALF_CONFIRM)) return;
              try {
                await completeTask(
                  task.id,
                  name.trim(),
                  notes.trim(),
                  user?.upn ?? '',
                  false,
                  eventKey.for(`${task.id}\n${name.trim()}\n${notes.trim()}`),
                );
              } catch (err) {
                setSaveError(err instanceof Error ? err.message : String(err));
                return;
              }
              eventKey.reset();
              onDone();
            }}
          >
            Complete task
          </ActionButton>
        </div>
    </Modal>
  );
}
